import { useState, useRef, useEffect, useCallback, useMemo } from 'react';

const TRACKS = [
  { title: 'Midnight Groove', meta: 'Side A · 03:42', seconds: 222 },
  { title: 'Velvet Static', meta: 'Side B · 04:18', seconds: 258 },
  { title: 'Lo-Fi Bloom', meta: 'Side A · 02:55', seconds: 175 },
  { title: 'Neon Cassette', meta: 'Side B · 05:03', seconds: 303 },
  { title: 'Golden Hour', meta: 'Side A · 03:29', seconds: 209 },
];

const KNOB_LABELS = ['VOL', 'BASS', 'TREB', 'TONE'];
const RPM_OPTIONS = [
  { label: '33', duration: '18s' },
  { label: '45', duration: '12s' },
  { label: '78', duration: '7s' },
];

// Different vinyl disc "records" - swappable
const DISCS = [
  {
    id: 'lavender',
    label: 'Lavender',
    vinyl: '#3a1150',
    paper: 'radial-gradient(circle at 50% 50%, #f7ecda 0%, #eddcc3 100%)',
    ring: '#d8c8b0',
    center: 'radial-gradient(circle at 40% 35%, #b8a5dc, #7d5eb0 80%)',
    textTop: '#7a6a94',
    textBottom: '#3a1150',
  },
  {
    id: 'cream',
    label: 'Cream',
    vinyl: '#2a1130',
    paper: 'radial-gradient(circle at 50% 50%, #fff5e0 0%, #f0d9b0 100%)',
    ring: '#c9ad83',
    center: 'radial-gradient(circle at 40% 35%, #f7cd6e, #d89838 80%)',
    textTop: '#8b6d3a',
    textBottom: '#2a1130',
  },
  {
    id: 'mint',
    label: 'Mint',
    vinyl: '#0e2f2a',
    paper: 'radial-gradient(circle at 50% 50%, #e8f2ec 0%, #cee5d5 100%)',
    ring: '#a8c8b3',
    center: 'radial-gradient(circle at 40% 35%, #8ecfae, #4a9d78 80%)',
    textTop: '#4a7a63',
    textBottom: '#0e2f2a',
  },
  {
    id: 'blush',
    label: 'Blush',
    vinyl: '#4a0f28',
    paper: 'radial-gradient(circle at 50% 50%, #fbe7ea 0%, #efc9d1 100%)',
    ring: '#d09aa6',
    center: 'radial-gradient(circle at 40% 35%, #f2a9b8, #c85e78 80%)',
    textTop: '#8b4a5c',
    textBottom: '#4a0f28',
  },
];

const fmt = (s: number) => {
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${r.toString().padStart(2, '0')}`;
};

type Note = { id: number; left: number; icon: string; size: number; hue: number; duration: number };
type Ripple = { id: number; x: number; y: number };

export default function Home() {
  const [playing, setPlaying] = useState(false);
  const [trackIdx, setTrackIdx] = useState(0);
  const [shuffle, setShuffle] = useState(false);
  const [knobValues, setKnobValues] = useState([65, 40, 78, 30]);
  const [faderValue, setFaderValue] = useState(55);
  const [leds, setLeds] = useState<boolean[]>([
    true, false, true,
    false, true, false,
    true, true, false,
  ]);
  const [rpmIdx, setRpmIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [side, setSide] = useState<'A' | 'B'>('A');
  const [vuLevels, setVuLevels] = useState<number[]>([40, 60, 30, 70, 50]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [tonearmDrag, setTonearmDrag] = useState(0);
  const [showHint, setShowHint] = useState(true);
  const [discIdx, setDiscIdx] = useState(0);
  const [discSwapping, setDiscSwapping] = useState(false);
  const [ripples, setRipples] = useState<Ripple[]>([]);
  const [pressedKey, setPressedKey] = useState<string | null>(null);
  const [powerOn, setPowerOn] = useState(true);

  const noteIdRef = useRef(0);
  const rippleIdRef = useRef(0);
  const tonearmRef = useRef<HTMLDivElement | null>(null);
  const dragStartRef = useRef<{ startY: number; startVal: number } | null>(null);

  const track = TRACKS[trackIdx];
  const rpm = RPM_OPTIONS[rpmIdx];
  const disc = DISCS[discIdx];

  const volValue = knobValues[0];
  const bassValue = knobValues[1];

  // ============ Actions ============
  const bumpKnob = (i: number, delta: number) => {
    setKnobValues((prev) =>
      prev.map((v, idx) => {
        if (idx !== i) return v;
        let next = v + delta;
        if (next > 100) next = 0;
        if (next < 0) next = 100;
        return next;
      }),
    );
  };

  const nextTrack = useCallback(() => {
    const nextIdx = shuffle
      ? Math.floor(Math.random() * TRACKS.length)
      : (trackIdx + 1) % TRACKS.length;
    setTrackIdx(nextIdx);
    setProgress(0);
  }, [shuffle, trackIdx]);

  const prevTrack = useCallback(() => {
    const nextIdx = (trackIdx - 1 + TRACKS.length) % TRACKS.length;
    setTrackIdx(nextIdx);
    setProgress(0);
  }, [trackIdx]);

  const toggleShuffle = () => setShuffle((s) => !s);
  const cycleRpm = useCallback(() => {
    setRpmIdx((i) => (i + 1) % RPM_OPTIONS.length);
  }, []);
  const toggleLed = (i: number) => {
    setLeds((prev) => prev.map((v, idx) => (idx === i ? !v : v)));
  };
  const handleFaderClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const pct = Math.max(0, Math.min(100, (y / rect.height) * 100));
    setFaderValue(Math.round(100 - pct));
  };
  const togglePlay = useCallback(() => {
    setPlaying((p) => !p);
  }, []);
  const flipSide = () => setSide((s) => (s === 'A' ? 'B' : 'A'));

  const swapDisc = useCallback(
    (dir: number = 1) => {
      if (discSwapping) return;
      setDiscSwapping(true);
      setPlaying(false);
      window.setTimeout(() => {
        setDiscIdx((i) => (i + dir + DISCS.length) % DISCS.length);
        setProgress(0);
        setDiscSwapping(false);
      }, 350);
    },
    [discSwapping],
  );

  const spawnRipple = (e: React.MouseEvent<HTMLElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const r: Ripple = {
      id: rippleIdRef.current++,
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
    setRipples((prev) => [...prev, r]);
    window.setTimeout(() => {
      setRipples((prev) => prev.filter((x) => x.id !== r.id));
    }, 700);
  };

  // ============ Effects ============
  // Progress ticker
  useEffect(() => {
    if (!playing) return;
    const step = 100 / track.seconds;
    const id = window.setInterval(() => {
      setProgress((p) => {
        const n = p + step / 4;
        if (n >= 100) {
          window.setTimeout(() => nextTrack(), 0);
          return 0;
        }
        return n;
      });
    }, 250);
    return () => window.clearInterval(id);
  }, [playing, track.seconds, nextTrack]);

  // VU meter — reacts to volume knob
  useEffect(() => {
    if (!playing) {
      setVuLevels([10, 10, 10, 10, 10]);
      return;
    }
    const id = window.setInterval(() => {
      const boost = volValue / 100;
      setVuLevels(() =>
        Array.from({ length: 5 }, () => 20 + Math.random() * 80 * boost),
      );
    }, 180);
    return () => window.clearInterval(id);
  }, [playing, volValue]);

  // Notes spawn — VOL controls RATE and SIZE
  useEffect(() => {
    if (!playing) return;
    const rate = Math.max(180, 1200 - volValue * 10); // higher vol → faster spawn
    const id = window.setInterval(() => {
      const icons = ['ri-music-2-fill', 'ri-music-fill', 'ri-heart-fill', 'ri-star-fill'];
      const sizeBase = 24 + (volValue / 100) * 46; // 24→70 px
      const size = sizeBase + Math.random() * 10;
      const spawnCount = volValue > 75 ? 2 : 1;
      const created: Note[] = [];
      for (let k = 0; k < spawnCount; k++) {
        // Spawn on the LEFT (5–30%) or RIGHT (70–95%) side only, keep the center clear so notes don't cover the title
        const goLeft = Math.random() < 0.5;
        const left = goLeft ? 5 + Math.random() * 25 : 70 + Math.random() * 25;
        const note: Note = {
          id: noteIdRef.current++,
          left,
          icon: icons[Math.floor(Math.random() * icons.length)],
          size,
          hue: Math.floor(Math.random() * 40) - 20,
          duration: 2.2 + Math.random() * 1.4,
        };
        created.push(note);
      }
      setNotes((prev) => [...prev, ...created]);
      created.forEach((n) => {
        window.setTimeout(() => {
          setNotes((prev) => prev.filter((x) => x.id !== n.id));
        }, n.duration * 1000 + 100);
      });
    }, rate);
    return () => window.clearInterval(id);
  }, [playing, volValue]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      setPressedKey(e.code);
      window.setTimeout(() => setPressedKey(null), 250);

      if (e.code === 'Space') {
        e.preventDefault();
        togglePlay();
        setShowHint(false);
      } else if (e.code === 'ArrowRight') {
        e.preventDefault();
        nextTrack();
      } else if (e.code === 'ArrowLeft') {
        e.preventDefault();
        prevTrack();
      } else if (e.code === 'ArrowUp') {
        e.preventDefault();
        bumpKnob(0, 10);
      } else if (e.code === 'ArrowDown') {
        e.preventDefault();
        bumpKnob(0, -10);
      } else if (e.key === 's' || e.key === 'S') {
        toggleShuffle();
      } else if (e.key === 'd' || e.key === 'D') {
        swapDisc(1);
      } else if (e.key === 'r' || e.key === 'R') {
        cycleRpm();
      } else if (e.key === 'f' || e.key === 'F') {
        flipSide();
      } else if (/^[1-5]$/.test(e.key)) {
        setTrackIdx(Number(e.key) - 1);
        setProgress(0);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [togglePlay, nextTrack, prevTrack, swapDisc, cycleRpm]);

  // Draggable tonearm
  const onTonearmPointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    dragStartRef.current = { startY: e.clientY, startVal: tonearmDrag };
  };
  const onTonearmPointerMove = (e: React.PointerEvent) => {
    if (!dragStartRef.current) return;
    const dy = e.clientY - dragStartRef.current.startY;
    const next = Math.max(-15, Math.min(15, dragStartRef.current.startVal + dy * 0.4));
    setTonearmDrag(next);
  };
  const onTonearmPointerUp = () => {
    if (dragStartRef.current) dragStartRef.current = null;
  };

  const seekProgress = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setProgress(pct);
  };

  const knobAngle = (v: number) => -135 + (v / 100) * 270;
  const faderHeight = 120;
  const faderKnobH = 22;
  const faderTopPx = ((100 - faderValue) / 100) * (faderHeight - faderKnobH);

  const screwPositions = [
    'top-3 left-3',
    'top-3 right-3',
    'bottom-3 left-3',
    'bottom-3 right-3',
  ];
  const rings = [78, 66, 54, 42];
  const topText = `JUKEBOX . , LTD  •  ${disc.label.toUpperCase()} EDITION`;
  const bottomText = `STUDIO x BEDROOM  •  SIDE ${side}`;

  const baseArmAngle = playing ? 160 : 135;
  const armAngle = baseArmAngle + tonearmDrag;

  // Machine breathes when playing (subtle pulse driven by BASS)
  const bodyBreath = playing
    ? `scale(${1 + (bassValue / 100) * 0.008})`
    : 'scale(1)';

  const keyLabels = useMemo(
    () => [
      { code: 'Space', label: 'PLAY' },
      { code: 'ArrowLeft', label: '◀' },
      { code: 'ArrowRight', label: '▶' },
      { code: 'KeyD', label: 'D · DISC' },
      { code: 'KeyR', label: 'R · RPM' },
      { code: 'KeyF', label: 'F · FLIP' },
      { code: 'KeyS', label: 'S · SHUFFLE' },
    ],
    [],
  );

  return (
    <div
      className="relative w-full h-screen min-h-[760px] overflow-hidden flex flex-col items-center px-6 pt-6 md:pt-8 pb-4"
      style={{ background: powerOn ? '#e9dcf5' : '#c9b8dc' }}
    >
      {/* soft radial glow */}
      <div
        className="pointer-events-none absolute inset-0 transition-opacity duration-500"
        style={{
          background:
            'radial-gradient(ellipse at 50% 35%, rgba(255,255,255,0.55) 0%, rgba(233,220,245,0) 55%)',
          opacity: powerOn ? 1 : 0.3,
        }}
      />

      {/* Hero heading */}
      <div className="relative w-full max-w-6xl flex-shrink-0 z-10 flex flex-col items-center">
        {/* Navbar */}
        <nav
          className="w-full flex items-center justify-between mb-6 md:mb-8 px-4 md:px-5 py-2.5 rounded-full"
          style={{
            background: 'rgba(255,255,255,0.55)',
            border: '1px solid rgba(58,17,80,0.15)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {/* Brand */}
          <a
            href="#"
            className="flex items-center gap-2 group cursor-pointer whitespace-nowrap"
            style={{ color: '#2a1130' }}
          >
            <span
              className="w-7 h-7 rounded-full flex items-center justify-center group-hover:rotate-45 transition-transform duration-500"
              style={{
                background:
                  'radial-gradient(circle at 35% 30%, #4a2168, #2a1130 80%)',
              }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: '#e9dcf5' }}
              />
            </span>
            <span
              className="text-lg leading-none"
              style={{ fontFamily: "'Fraunces', serif", fontWeight: 800 }}
            >
              Jukebox
            </span>
            <span
              className="text-[9px] tracking-[0.25em] font-bold pt-0.5"
              style={{ color: '#7d5eb0' }}
            >
              .,LTD
            </span>
          </a>

          {/* Center links */}
          <div
            className="hidden md:flex items-center gap-7 text-[11px] tracking-[0.25em] font-bold"
            style={{ color: '#3a1150' }}
          >
            {['RECORDS', 'PLAYLIST', 'STUDIO', 'STORIES'].map((item, i) => (
              <a
                key={item}
                href="#"
                className="relative py-1 hover:text-[#7d5eb0] transition-colors cursor-pointer whitespace-nowrap"
              >
                {item}
                {i === 0 && (
                  <span
                    className="absolute -top-1 -right-2 w-1.5 h-1.5 rounded-full dot-pulse"
                    style={{ background: '#e26a6a' }}
                  />
                )}
              </a>
            ))}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2">
            <span
              className="hidden lg:inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] font-bold"
              style={{ color: '#5a3f7e' }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{
                  background: playing ? '#7fc267' : 'rgba(58,17,80,0.35)',
                  boxShadow: playing ? '0 0 6px rgba(127,194,103,0.9)' : 'none',
                }}
              />
              {playing ? 'ON AIR' : 'STANDBY'}
            </span>
            <button
              type="button"
              onClick={togglePlay}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[10px] tracking-[0.25em] font-bold cursor-pointer hover:brightness-110 active:scale-95 transition-all whitespace-nowrap"
              style={{ background: '#2a1130', color: '#f7ecda' }}
            >
              <i
                className={playing ? 'ri-pause-fill' : 'ri-play-fill'}
                style={{ fontSize: '13px' }}
              />
              TUNE IN
            </button>
          </div>
        </nav>

        <h1
          className="text-center text-4xl md:text-6xl lg:text-[76px] leading-[1] mb-4 tracking-tight"
          style={{
            fontFamily: "'Fraunces', 'Times New Roman', serif",
            color: '#2a1130',
            fontWeight: 800,
            fontOpticalSizing: 'auto',
          }}
        >
          <span className="block">Spin the Vinyl,</span>
          <span
            className="block italic"
            style={{ color: '#7d5eb0', fontWeight: 700 }}
          >
            Feel the Groove.
          </span>
        </h1>

        <p
          className="text-center text-[13px] md:text-sm max-w-md mx-auto leading-relaxed mb-5"
          style={{ color: '#5a3f7e', fontFamily: "'Inter', sans-serif" }}
        >
          A hand-crafted lo-fi jukebox. Every knob, switch, and blinking light
          is yours to play with.
        </p>

      </div>

      <div className="flex-1 min-h-[24px] md:min-h-[48px]" />

      {/* Jukebox */}
      <div
        className="relative w-full max-w-6xl rounded-[2rem] p-2.5 flex-shrink-0 z-10 transition-transform duration-300"
        style={{
          background: 'linear-gradient(145deg, #a78bd9, #8a6fc0)',
          transform: bodyBreath,
        }}
      >
        {/* Floating notes drifting sideways — pastel colors, kept off the title */}
        <div className="pointer-events-none absolute inset-x-0 bottom-full h-[220px] overflow-visible z-30">
          {notes.map((n) => {
            // Pastel hue — lower saturation + higher lightness so it doesn't clash with text
            const hue = ((n.id * 47) + n.hue * 4) % 360;
            const noteColor = `hsl(${hue}, 55%, 72%)`;
            const glowSoft = `hsl(${hue}, 60%, 85%)`;
            // Notes on the left half drift LEFT, notes on the right half drift RIGHT — they "blow outward" away from the center
            return (
              <span
                key={n.id}
                className="note-rise"
                style={{
                  left: `${n.left}%`,
                  animationDuration: `${n.duration}s`,
                }}
              >
                <i
                  className={n.icon}
                  style={{
                    fontSize: `${n.size}px`,
                    color: noteColor,
                    // Soft pastel bloom + a light trailing wisp — no harsh neon glow
                    textShadow: [
                      `0 0 8px ${glowSoft}80`,
                      `0 0 18px ${glowSoft}55`,
                      `0 10px 14px ${glowSoft}44`,
                      `0 18px 22px ${glowSoft}22`,
                    ].join(', '),
                  }}
                />
              </span>
            );
          })}
        </div>

        <div
          className="relative rounded-[1.75rem] p-5 md:p-8"
          style={{
            background: 'linear-gradient(160deg, #b39ce0 0%, #9578c7 100%)',
          }}
        >
          {/* Corner screws */}
          {screwPositions.map((pos) => (
            <div
              key={pos}
              className={`absolute ${pos} w-6 h-6 rounded-full flex items-center justify-center`}
              style={{
                background:
                  'radial-gradient(circle at 30% 30%, #d4c1ec, #7d5eb0)',
              }}
            >
              <div
                className="w-3.5 h-[2px] rounded-full"
                style={{ background: '#3d1f5c', transform: 'rotate(45deg)' }}
              />
            </div>
          ))}

          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 items-center">
            {/* LEFT: Vinyl + Tonearm */}
            <div className="relative w-[280px] h-[280px] md:w-[340px] md:h-[340px] flex-shrink-0">
              {/* Disc swap buttons */}
              <button
                onClick={() => swapDisc(-1)}
                aria-label="Previous disc"
                className="absolute -left-2 top-1/2 -translate-y-1/2 z-30 w-8 h-8 rounded-full flex items-center justify-center cursor-pointer hover:scale-110 active:scale-95 transition-transform"
                style={{
                  background: '#f7ecda',
                  border: '2px solid #2a1130',
                  color: '#2a1130',
                }}
              >
                <i className="ri-arrow-left-s-line text-lg" />
              </button>
              <button
                onClick={() => swapDisc(1)}
                aria-label="Next disc"
                className="absolute -right-2 top-1/2 -translate-y-1/2 z-30 w-8 h-8 rounded-full flex items-center justify-center cursor-pointer hover:scale-110 active:scale-95 transition-transform"
                style={{
                  background: '#f7ecda',
                  border: '2px solid #2a1130',
                  color: '#2a1130',
                }}
              >
                <i className="ri-arrow-right-s-line text-lg" />
              </button>

              {/* Vinyl disc */}
              <div
                onClick={(e) => {
                  spawnRipple(e);
                  cycleRpm();
                }}
                className={`absolute inset-0 rounded-full cursor-pointer overflow-hidden ${
                  playing ? 'vinyl-spinning' : 'vinyl-stopped'
                } ${discSwapping ? 'disc-swap' : 'disc-in'}`}
                style={
                  {
                    background: disc.vinyl,
                    padding: '3.5%',
                    ['--spin-duration' as string]: rpm.duration,
                  } as React.CSSProperties
                }
                title="Click to change RPM"
              >
                <div
                  className="relative w-full h-full rounded-full flex items-center justify-center"
                  style={{ background: disc.paper }}
                >
                  {rings.map((r) => (
                    <div
                      key={r}
                      className="absolute rounded-full"
                      style={{
                        width: `${r}%`,
                        height: `${r}%`,
                        border: `1px solid ${disc.ring}`,
                      }}
                    />
                  ))}

                  <svg
                    className="absolute inset-0 w-full h-full"
                    viewBox="0 0 500 500"
                  >
                    <defs>
                      <path
                        id="arcTopPath"
                        d="M 90 260 A 165 165 0 0 1 410 260"
                      />
                      <path
                        id="arcBottomPath"
                        d="M 100 260 A 155 155 0 0 0 400 260"
                      />
                    </defs>
                    <text
                      fontSize="18"
                      fill={disc.textTop}
                      fontFamily="'Caveat', cursive"
                      fontWeight="600"
                      letterSpacing="2"
                    >
                      <textPath href="#arcTopPath" startOffset="12%">
                        {topText}
                      </textPath>
                    </text>
                    <text
                      fontSize="16"
                      fill={disc.textBottom}
                      fontFamily="'Caveat', cursive"
                      fontWeight="700"
                      letterSpacing="2"
                    >
                      <textPath href="#arcBottomPath" startOffset="18%">
                        {bottomText}
                      </textPath>
                    </text>
                  </svg>

                  {/* Center label */}
                  <div
                    className="relative rounded-full flex items-center justify-center"
                    style={{
                      width: '36%',
                      height: '36%',
                      background: disc.center,
                      boxShadow: 'inset 0 -3px 8px rgba(42,17,48,0.35)',
                    }}
                  >
                    <div className="absolute text-center" style={{ color: '#f7ecda' }}>
                      <div
                        style={{
                          fontFamily: "'Caveat', cursive",
                          fontWeight: 700,
                          fontSize: '24px',
                          lineHeight: 1,
                        }}
                      >
                        {rpm.label}
                      </div>
                      <div
                        style={{
                          fontFamily: "'Inter', sans-serif",
                          fontSize: '8px',
                          letterSpacing: '2px',
                        }}
                      >
                        RPM
                      </div>
                    </div>
                    <div
                      className="rounded-full absolute"
                      style={{
                        width: '9%',
                        height: '9%',
                        background: '#2a1130',
                        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.6)',
                      }}
                    />
                  </div>

                  {/* Click ripples */}
                  {ripples.map((r) => (
                    <span
                      key={r.id}
                      className="ripple absolute rounded-full pointer-events-none"
                      style={{
                        left: r.x,
                        top: r.y,
                        background:
                          'radial-gradient(circle, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0) 70%)',
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Tonearm */}
              <div className="absolute z-20" style={{ top: '4%', right: '4%' }}>
                <div
                  className="relative rounded-full flex items-center justify-center"
                  style={{
                    width: '62px',
                    height: '62px',
                    background: '#f3e8d8',
                    border: '4px solid #2a1130',
                  }}
                >
                  <div
                    className="rounded-full"
                    style={{ width: '10px', height: '10px', background: '#2a1130' }}
                  />
                </div>

                <div
                  ref={tonearmRef}
                  className="absolute tonearm-anim"
                  style={{
                    top: '31px',
                    left: '31px',
                    width: '190px',
                    height: '10px',
                    transform: `rotate(${armAngle}deg)`,
                    transformOrigin: '0% 50%',
                  }}
                >
                  <div
                    className="absolute rounded-full"
                    style={{
                      top: '2px',
                      left: '6px',
                      right: '44px',
                      height: '6px',
                      background:
                        'linear-gradient(to bottom, #ffffff 0%, #e8dcf5 100%)',
                    }}
                  />
                  <button
                    onClick={togglePlay}
                    onPointerDown={onTonearmPointerDown}
                    onPointerMove={onTonearmPointerMove}
                    onPointerUp={onTonearmPointerUp}
                    onPointerCancel={onTonearmPointerUp}
                    aria-label={playing ? 'Pause' : 'Play'}
                    title="Click to play/pause, drag to adjust"
                    className="absolute rounded-full flex items-center justify-center hover:scale-110 active:scale-95 transition-transform cursor-grab active:cursor-grabbing whitespace-nowrap select-none"
                    style={{
                      width: '52px',
                      height: '52px',
                      right: '-8px',
                      top: '-21px',
                      background:
                        'radial-gradient(circle at 35% 30%, #ffffff, #ede0f7)',
                      border: '3px solid #2a1130',
                      touchAction: 'none',
                    }}
                  >
                    {playing ? (
                      <div className="flex gap-1.5 pointer-events-none">
                        <div className="rounded-sm" style={{ width: '4px', height: '18px', background: '#3a1150' }} />
                        <div className="rounded-sm" style={{ width: '4px', height: '18px', background: '#3a1150' }} />
                      </div>
                    ) : (
                      <div
                        className="pointer-events-none"
                        style={{
                          width: 0,
                          height: 0,
                          borderTop: '9px solid transparent',
                          borderBottom: '9px solid transparent',
                          borderLeft: '14px solid #3a1150',
                          marginLeft: '4px',
                        }}
                      />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* RIGHT: Panel */}
            <div className="flex-1 w-full flex flex-col gap-4">
              {/* Title bar */}
              <div
                className="rounded-2xl px-6 py-4 flex items-center justify-between"
                style={{
                  background:
                    'linear-gradient(135deg, #2a1130 0%, #3a1150 100%)',
                }}
              >
                <h2
                  className="text-white text-2xl md:text-3xl font-bold"
                  style={{ fontFamily: "'Caveat', cursive" }}
                >
                  Jukebox.,ltd
                </h2>
                <div className="flex items-center gap-3">
                  <button
                    onClick={flipSide}
                    className="text-[10px] font-bold tracking-widest px-2.5 py-1 rounded-full cursor-pointer hover:brightness-110 active:scale-95 transition-all whitespace-nowrap"
                    style={{
                      background: '#f7ecda',
                      color: '#2a1130',
                      fontFamily: "'Inter', sans-serif",
                    }}
                    title="Flip side (F)"
                  >
                    SIDE {side} ⇋
                  </button>
                  <div className="flex gap-2">
                    <button
                      className="w-3 h-3 rounded-full cursor-pointer hover:scale-125 transition-transform"
                      style={{ background: '#e0c04a' }}
                      aria-label="Minimize"
                    />
                    <button
                      className="w-3 h-3 rounded-full cursor-pointer hover:scale-125 transition-transform"
                      style={{ background: '#7fc267' }}
                      aria-label="Maximize"
                    />
                    <button
                      onClick={() => {
                        setPowerOn((p) => !p);
                        setPlaying(false);
                      }}
                      className="w-3 h-3 rounded-full cursor-pointer hover:scale-125 transition-transform"
                      style={{ background: '#e26a6a' }}
                      aria-label="Power"
                      title="Power"
                    />
                  </div>
                </div>
              </div>

              {/* Control panel */}
              <div
                className="rounded-2xl p-5 flex items-center justify-between gap-5 flex-wrap"
                style={{
                  background:
                    'linear-gradient(160deg, #f7ecda 0%, #ecdfc5 100%)',
                }}
              >
                {/* Knobs */}
                <div className="flex gap-4">
                  {KNOB_LABELS.map((label, i) => {
                    // VOL knob grows visually with value
                    const growth = i === 0 ? 1 + (knobValues[i] / 100) * 0.35 : 1;
                    return (
                      <div
                        key={label}
                        className="flex flex-col items-center gap-1"
                      >
                        <button
                          onClick={(e) => {
                            spawnRipple(e);
                            bumpKnob(i, 15);
                          }}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            bumpKnob(i, -15);
                          }}
                          onWheel={(e) => {
                            bumpKnob(i, e.deltaY > 0 ? -10 : 10);
                          }}
                          aria-label={`${label} ${knobValues[i]}%`}
                          title={i === 0 ? 'Volume — bigger = more/bigger notes' : label}
                          className={`relative rounded-full flex items-center justify-center cursor-pointer hover:brightness-110 active:scale-95 transition-all whitespace-nowrap ${
                            knobValues[i] > 70 ? 'knob-glow' : ''
                          }`}
                          style={{
                            width: '48px',
                            height: '48px',
                            background:
                              'radial-gradient(circle at 35% 30%, #4a2168, #2a1130 80%)',
                            boxShadow:
                              'inset 0 -2px 4px rgba(0,0,0,0.4), 0 2px 3px rgba(42,17,48,0.2)',
                            transform: `scale(${growth})`,
                            transitionProperty: 'transform, filter',
                          }}
                        >
                          {[...Array(9)].map((_, t) => {
                            const angle = -135 + (t / 8) * 270;
                            const active = t <= (knobValues[i] / 100) * 8;
                            return (
                              <div
                                key={t}
                                className="absolute rounded-full"
                                style={{
                                  width: '2px',
                                  height: '3px',
                                  background: active ? '#e9dcf5' : '#5a3f7e',
                                  top: '2px',
                                  left: '50%',
                                  transformOrigin: '50% 22px',
                                  transform: `translateX(-50%) rotate(${angle}deg)`,
                                }}
                              />
                            );
                          })}
                          <div
                            className="absolute rounded-full transition-transform"
                            style={{
                              width: '3px',
                              height: '14px',
                              background: '#e9dcf5',
                              top: '8px',
                              left: '50%',
                              transformOrigin: '50% 16px',
                              transform: `translateX(-50%) rotate(${knobAngle(
                                knobValues[i],
                              )}deg)`,
                            }}
                          />
                        </button>
                        <span
                          className="text-[9px] font-bold tracking-widest"
                          style={{ color: '#3a1150' }}
                        >
                          {label}
                        </span>
                        <span
                          className="text-[9px] font-semibold"
                          style={{ color: '#7d5eb0' }}
                        >
                          {knobValues[i]}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Fader + VU Meter + LED grid */}
                <div className="flex items-end gap-4">
                  <div className="flex flex-col items-center gap-1">
                    <div
                      onClick={handleFaderClick}
                      className="relative rounded-full cursor-pointer"
                      style={{
                        width: '14px',
                        height: `${faderHeight}px`,
                        background: '#2a1130',
                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.5)',
                      }}
                    >
                      <div
                        className="absolute left-0 right-0 bottom-0 rounded-full transition-all"
                        style={{
                          height: `${faderValue}%`,
                          background:
                            'linear-gradient(to top, #7d5eb0, #b39ce0)',
                        }}
                      />
                      <div
                        className="absolute rounded-md fader-knob"
                        style={{
                          width: '28px',
                          height: `${faderKnobH}px`,
                          left: '-7px',
                          top: `${faderTopPx}px`,
                          background:
                            'linear-gradient(180deg, #b39ce0 0%, #7d5eb0 100%)',
                          boxShadow:
                            'inset 0 -2px 3px rgba(42,17,48,0.3), 0 1px 2px rgba(0,0,0,0.2)',
                          pointerEvents: 'none',
                        }}
                      >
                        <div
                          className="absolute left-0 right-0 top-1/2 -translate-y-1/2 mx-1 h-[2px] rounded-full"
                          style={{ background: '#3a1150' }}
                        />
                      </div>
                    </div>
                    <span
                      className="text-[9px] font-bold tracking-widest"
                      style={{ color: '#3a1150' }}
                    >
                      MIX
                    </span>
                  </div>

                  {/* VU meter bars */}
                  <div className="flex flex-col items-center gap-1">
                    <div
                      className="rounded-lg flex items-end justify-between gap-[6px] px-4 py-2"
                      style={{
                        width: '216px',
                        height: `${faderHeight}px`,
                        background: '#2a1130',
                      }}
                      title="VU Meter"
                    >
                      {vuLevels.map((lv, i) => {
                        const h = Math.max(6, (lv / 100) * (faderHeight - 16));
                        // Rainbow palette across the 5 bars
                        const rainbow = [
                          '#ff5ea8', // pink
                          '#ff9a3c', // orange
                          '#f7d84b', // yellow
                          '#5ed6a2', // mint green
                          '#5ec8ff', // sky blue
                        ];
                        const color = rainbow[i % rainbow.length];
                        return (
                          <div
                            key={i}
                            className="rounded-sm vu-bar flex-1"
                            style={{
                              height: `${h}px`,
                              background: `linear-gradient(to top, ${color}, #ffffff)`,
                            }}
                          />
                        );
                      })}
                    </div>
                    <span
                      className="text-[9px] font-bold tracking-widest"
                      style={{ color: '#3a1150' }}
                    >
                      VU
                    </span>
                  </div>

                  {/* LED grid */}
                  <div className="flex flex-col items-center gap-1">
                    <div
                      className="rounded-lg grid grid-cols-3 gap-1.5 p-2"
                      style={{
                        width: '64px',
                        height: '84px',
                        background: '#2a1130',
                      }}
                    >
                      {leds.map((on, i) => (
                        <button
                          key={i}
                          onClick={() => toggleLed(i)}
                          aria-label={`LED ${i + 1}`}
                          className={`rounded-full cursor-pointer transition-all hover:scale-110 ${
                            on && playing ? 'led-pulse' : ''
                          }`}
                          style={{
                            width: '12px',
                            height: '12px',
                            background: on
                              ? '#b39ce0'
                              : 'rgba(179,156,224,0.25)',
                            boxShadow: on
                              ? '0 0 6px rgba(179,156,224,0.9)'
                              : 'none',
                            animationDelay: `${i * 0.12}s`,
                          }}
                        />
                      ))}
                    </div>
                    <span
                      className="text-[9px] font-bold tracking-widest"
                      style={{ color: '#3a1150' }}
                    >
                      EQ
                    </span>
                  </div>
                </div>
              </div>

              {/* Track info + progress + transport controls */}
              <div className="flex flex-col gap-2 px-1">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p
                      className="text-[#2a1130] text-[10px] font-bold tracking-[0.2em]"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      NOW PLAYING · {track.meta}
                    </p>
                    <p
                      className="text-white text-lg md:text-xl truncate"
                      style={{ fontFamily: "'Caveat', cursive" }}
                    >
                      {playing ? `${track.title} — Side ${side}` : 'Press play to spin'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={prevTrack}
                      aria-label="Previous"
                      className="w-9 h-9 flex items-center justify-center rounded-full text-white/90 hover:text-white hover:bg-white/10 cursor-pointer transition-colors"
                    >
                      <i className="ri-skip-back-fill text-xl" />
                    </button>
                    <button
                      onClick={togglePlay}
                      aria-label={playing ? 'Pause' : 'Play'}
                      className="w-10 h-10 flex items-center justify-center rounded-full cursor-pointer hover:brightness-110 active:scale-95 transition-all"
                      style={{ background: '#f7ecda', color: '#2a1130' }}
                    >
                      <i className={playing ? 'ri-pause-fill text-xl' : 'ri-play-fill text-xl'} />
                    </button>
                    <button
                      onClick={toggleShuffle}
                      aria-label="Shuffle"
                      className={`w-9 h-9 flex items-center justify-center rounded-full cursor-pointer transition-colors ${
                        shuffle
                          ? 'text-[#2a1130]'
                          : 'text-white/90 hover:text-white hover:bg-white/10'
                      }`}
                      style={shuffle ? { background: '#f7ecda' } : undefined}
                    >
                      <i className="ri-shuffle-line text-lg" />
                    </button>
                    <button
                      onClick={nextTrack}
                      aria-label="Next"
                      className="w-9 h-9 flex items-center justify-center rounded-full text-white/90 hover:text-white hover:bg-white/10 cursor-pointer transition-colors"
                    >
                      <i className="ri-skip-forward-fill text-xl" />
                    </button>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="flex items-center gap-3">
                  <span
                    className="text-[10px] font-mono tabular-nums"
                    style={{ color: '#f7ecda' }}
                  >
                    {fmt((progress / 100) * track.seconds)}
                  </span>
                  <div
                    onClick={seekProgress}
                    className="relative flex-1 h-2 rounded-full cursor-pointer group"
                    style={{ background: 'rgba(42,17,48,0.35)' }}
                    title="Click to seek"
                  >
                    <div
                      className="absolute left-0 top-0 bottom-0 rounded-full transition-[width] duration-150"
                      style={{
                        width: `${progress}%`,
                        background:
                          'linear-gradient(to right, #f7ecda, #ffffff)',
                      }}
                    />
                    <div
                      className="absolute top-1/2 -translate-y-1/2 rounded-full transition-[left] duration-150 opacity-0 group-hover:opacity-100"
                      style={{
                        left: `calc(${progress}% - 6px)`,
                        width: '12px',
                        height: '12px',
                        background: '#ffffff',
                        boxShadow: '0 0 6px rgba(255,255,255,0.9)',
                      }}
                    />
                  </div>
                  <span
                    className="text-[10px] font-mono tabular-nums"
                    style={{ color: '#f7ecda' }}
                  >
                    {fmt(track.seconds)}
                  </span>
                </div>

                {/* Track dots — click to pick, and 1..5 shortcuts */}
                <div className="flex items-center gap-2 pt-1">
                  <span
                    className="text-[9px] font-bold tracking-[0.25em]"
                    style={{ color: '#f7ecda', opacity: 0.85 }}
                  >
                    TRACK
                  </span>
                  {TRACKS.map((t, i) => (
                    <button
                      key={t.title}
                      onClick={() => {
                        setTrackIdx(i);
                        setProgress(0);
                      }}
                      className="group flex items-center gap-1 cursor-pointer"
                      title={`${i + 1}. ${t.title}`}
                    >
                      <span
                        className={`rounded-full transition-all ${
                          i === trackIdx ? 'w-3 h-3' : 'w-2 h-2 opacity-50 group-hover:opacity-90'
                        }`}
                        style={{
                          background: i === trackIdx ? '#f7ecda' : 'rgba(247,236,218,0.6)',
                          boxShadow:
                            i === trackIdx && playing
                              ? '0 0 8px rgba(247,236,218,0.9)'
                              : 'none',
                        }}
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom keyboard cheatsheet — reactive */}
      <div
        className={`mt-3 md:mt-4 flex-shrink-0 z-10 flex items-center gap-2 flex-wrap justify-center transition-opacity ${
          showHint ? 'opacity-100' : 'opacity-80'
        }`}
        style={{ color: '#5a3f7e', fontFamily: "'Inter', sans-serif" }}
      >
        {keyLabels.map((k) => (
          <span
            key={k.code}
            className={`kbd-chip ${pressedKey === k.code ? 'kbd-chip-active' : ''}`}
          >
            {k.label}
          </span>
        ))}
        <span
          className="kbd-chip"
          title="Number keys 1–5"
        >
          1..5 · JUMP
        </span>
      </div>
    </div>
  );
}